"use client";

import React from "react";
import { Landmark, Stethoscope, Scale, Wrench, Zap, ShoppingBag, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
const sectorData = [{
  title: "Accountants & Financial Services",
  description: "Target high-intent clients searching for tax planning, auditing, and corporate accountancy services in your region.",
  icon: Landmark,
  highlights: ["Local map pack optimization", "High-value commercial leads"],
  servicePath: "/B2BSeoLeadGenerationPage"
}, {
  title: "Dentists & Healthcare Clinics",
  description: "Capture local patients seeking private dental treatments, cosmetic procedures, and specialized health services.",
  icon: Stethoscope,
  highlights: ["Treatment-focused keyword targeting", "Google Business Profile authority"],
  servicePath: "/LocalSEOPage"
}, {
  title: "Solicitors & Legal Practices",
  description: "Generate qualified legal inquiries across family law, conveyancing, corporate law, and litigation.",
  icon: Scale,
  highlights: ["High-trust content strategy", "E-E-A-T authority building"],
  servicePath: "/LocalSEOPage"
}, {
  title: "Plumbers & HVAC Engineers",
  description: "Dominate local map listings and capture urgent calls for boiler repairs, installations, and commercial plumbing.",
  icon: Wrench,
  highlights: ["Immediate local call generation", "Geo-targeted landing pages"],
  servicePath: "/LocalSEOPage"
}, {
  title: "Electricians & Trade Services",
  description: "Secure consistent domestic and commercial electrical contracts through targeted local search visibility.",
  icon: Zap,
  highlights: ["Service area optimization", "Review & reputation signals"],
  servicePath: "/LocalSEOPage"
}, {
  title: "Ecommerce & Retail Brands",
  description: "Scale organic revenue by optimizing product collections, faceted navigation, and technical store architecture.",
  icon: ShoppingBag,
  highlights: ["Technical SEO & site speed", "High-converting product keywords"],
  servicePath: "/EcommerceSeoPage"
}];
export default function Sectors() {
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border-transparent">
            Sectors Served
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight mb-4 text-foreground">
            Bespoke SEO Strategies for Your Specific Sector
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every industry has distinct buyer behaviors, compliance factors, and search queries. You work directly with me to craft a tailored SEO strategy designed for high-value client acquisition in your market.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {sectorData.map((sector, i) => {
          const IconComponent = sector.icon;
          return <motion.div key={i} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="h-full">
                <Card className="h-full bg-card text-card-foreground border border-border rounded-xl shadow-xs hover:shadow-md transition-[border-color,box-shadow] hover:border-primary/40 flex flex-col justify-between group">
                  <CardHeader className="pb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-xl font-bold font-serif text-foreground">
                      {sector.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {sector.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 pb-4 flex-grow">
                    <div className="space-y-2 pt-2 border-t border-border/60">
                      {sector.highlights.map((item, idx) => <div key={idx} className="flex items-center text-xs text-muted-foreground gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span>{item}</span>
                        </div>)}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 pb-6">
                    <Button variant="ghost" size="sm" asChild className="p-0 text-primary hover:text-primary/80 hover:bg-transparent font-medium group/btn text-sm">
                      <Link to={sector.servicePath} className="inline-flex items-center gap-1.5">
                        <span>Explore sector SEO</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-12 md:mt-16 bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 md:p-10 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Multi-Sector Hands-on Expertise</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              {"Don's see your specific industry listed?"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              I work with a diverse range of UK and international businesses. Get in touch directly to discuss how I can tailor an SEO strategy for your niche.
            </p>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <Button size="lg" className="w-full md:w-auto font-semibold px-6" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}